import React from 'react';
import useBuilderStore from '../store/useBuilderStore';
import TemplateManager from './TemplateManager';

const ControlGroup = ({ title, children }) => (
  <div className="bg-white/5 rounded-[2.5rem] border border-white/5 p-8 shadow-2xl space-y-6 transition-all hover:bg-white/[0.07] hover:border-white/10 group/item">
    {title && <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.3em] border-b border-white/5 pb-4 group-hover/item:text-[#8B3DFF] transition-colors">{title}</h4>}
    {children}
  </div>
);

const Label = ({ children }) => (
  <label className="text-[10px] font-black text-slate-400 uppercase block mb-2.5 tracking-[0.1em]">{children}</label>
);

const PropertyEditor = () => {
  const { selectedElement, layout, globalSettings, setGlobalSettings, updateBlock, updateRow, removeBlock, removeRow } = useBuilderStore();

  const getSelectedData = () => {
    if (!selectedElement) return null;
    if (selectedElement.type === 'row') {
      return layout.find(r => r.id === selectedElement.id);
    }
    if (selectedElement.type === 'block') {
      for (const row of layout) {
        for (const col of row.columns) {
          const block = col.blocks.find(b => b.id === selectedElement.id);
          if (block) return block;
        }
      }
    }
    return null;
  };

  const data = getSelectedData();

  const handleUpdate = (key, value) => {
    if (selectedElement.type === 'block') {
      updateBlock(selectedElement.id, { [key]: value });
    } else if (selectedElement.type === 'row') {
      updateRow(selectedElement.id, { [key]: value });
    }
  };

  const renderSpacingControls = (target) => (
    <div className="space-y-8">
      <div>
        <div className="flex justify-between items-center mb-4">
          <Label>Margin</Label>
        </div>
        <div className="grid grid-cols-4 gap-3 bg-black/20 p-5 rounded-[2rem] border border-white/5 shadow-inner">
          {['marginTop', 'marginRight', 'marginBottom', 'marginLeft'].map((key, i) => (
            <div key={key} className="flex flex-col items-center">
              <span className="text-[8px] font-black text-slate-500 mb-2.5">{['TOP', 'RGT', 'BTM', 'LFT'][i]}</span>
              <input 
                type="number" 
                value={target[key] || 0}
                onChange={(e) => handleUpdate(key, parseInt(e.target.value) || 0)}
                className="w-full h-10 text-xs font-black border border-white/5 bg-white/5 text-white rounded-xl text-center focus:ring-2 focus:ring-[#8B3DFF] outline-none transition-all shadow-sm"
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label>Padding (Inner)</Label>
        <div className="grid grid-cols-4 gap-3 bg-black/20 p-5 rounded-[2rem] border border-white/5 shadow-inner">
          {['innerPaddingTop', 'innerPaddingRight', 'innerPaddingBottom', 'innerPaddingLeft'].map((key, i) => (
            <div key={key} className="flex flex-col items-center">
              <span className="text-[8px] font-black text-slate-500 mb-2.5">{['TOP', 'RGT', 'BTM', 'LFT'][i]}</span>
              <input 
                type="number" 
                value={target[key] || 0}
                onChange={(e) => handleUpdate(key, parseInt(e.target.value) || 0)}
                className="w-full h-10 text-xs font-black border border-white/5 bg-white/5 text-white rounded-xl text-center focus:ring-2 focus:ring-[#8B3DFF] outline-none transition-all shadow-sm"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (!selectedElement) {
    return (
      <div className="p-4 space-y-6">
        <ControlGroup title="Global Palette">
          <div className="flex items-center justify-between p-3.5 hover:bg-white/5 rounded-2xl transition-all border border-transparent hover:border-white/5">
            <Label>Canvas Fill</Label>
            <div className="flex items-center gap-4">
              <span className="text-[10px] font-black font-mono text-slate-500 uppercase tracking-widest">{globalSettings.pageColor}</span>
              <div className="relative w-9 h-9 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl ring-4 ring-black/20">
                <input type="color" value={globalSettings.pageColor} onChange={(e) => setGlobalSettings({ pageColor: e.target.value })} className="absolute inset-[-50%] w-[200%] h-[200%] cursor-pointer border-none bg-transparent" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between p-3.5 hover:bg-white/5 rounded-2xl transition-all border border-transparent hover:border-white/5">
            <Label>Container Fill</Label>
            <div className="flex items-center gap-4">
              <span className="text-[10px] font-black font-mono text-slate-500 uppercase tracking-widest">{globalSettings.containerColor}</span>
              <div className="relative w-9 h-9 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl ring-4 ring-black/20">
                <input type="color" value={globalSettings.containerColor} onChange={(e) => setGlobalSettings({ containerColor: e.target.value })} className="absolute inset-[-50%] w-[200%] h-[200%] cursor-pointer border-none bg-transparent" />
              </div>
            </div>
          </div>
        </ControlGroup>

        <ControlGroup title="Container Settings">
          <div className="flex justify-between items-center mb-5">
             <Label>Width</Label>
             <span className="text-[10px] font-black text-white bg-[#8B3DFF] px-3.5 py-1.5 rounded-full shadow-[0_4px_12px_rgba(139,61,255,0.4)]">{globalSettings.containerWidth}PX</span>
          </div>
          <input 
            type="range" 
            min="320" max="800" step="10"
            value={globalSettings.containerWidth}
            onChange={(e) => setGlobalSettings({ containerWidth: parseInt(e.target.value) || 600 })}
            className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#8B3DFF]"
          />
        </ControlGroup>

        <ControlGroup title="Library">
          <TemplateManager />
        </ControlGroup>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
      <div className="flex justify-between items-center px-3 mb-2">
        <div className="flex items-center gap-4">
          <div className="w-2.5 h-10 bg-gradient-to-b from-[#8B3DFF] to-[#7025e6] rounded-full shadow-[0_0_15px_rgba(139,61,255,0.3)]" />
          <h3 className="text-[12px] font-black text-white uppercase tracking-[0.25em]">
            {selectedElement.type}
          </h3>
        </div>
        <span className="text-[9px] font-black text-slate-600 font-mono tracking-[0.2em] bg-white/5 border border-white/5 px-3 py-1.5 rounded-full uppercase italic">#{String(selectedElement.id).slice(-6)}</span>
      </div>

      {(data.type === 'heading' || data.type === 'text') && (
        <ControlGroup title="Typography">
          <div>
            <Label>Message Content</Label>
            <textarea 
              value={data.text}
              onChange={(e) => handleUpdate('text', e.target.value)}
              rows={6}
              className="w-full px-6 py-5 text-sm border border-white/5 rounded-[2rem] resize-none focus:ring-4 focus:ring-[#8B3DFF]/10 focus:border-[#8B3DFF] outline-none bg-black/20 text-white transition-all shadow-inner placeholder:text-slate-700 font-medium leading-relaxed"
              placeholder="Start typing..."
            />          </div>
          
          <div className="flex justify-between items-center bg-black/20 p-5 rounded-[2rem] border border-white/5 shadow-inner">
            <Label>Text Tint</Label>
            <div className="flex items-center gap-4">
              <span className="text-[10px] font-black font-mono uppercase text-slate-500 tracking-widest">{data.color}</span>
              <div className="relative w-8 h-8 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl">
                 <input type="color" value={data.color} onChange={(e) => handleUpdate('color', e.target.value)} className="absolute inset-[-50%] w-[200%] h-[200%] cursor-pointer border-none bg-transparent" />
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
               <Label>Size</Label>
               <span className="text-[10px] font-black text-[#8B3DFF] bg-[#8B3DFF]/10 px-3 py-1 rounded-full border border-[#8B3DFF]/20">{data.fontSize}PX</span>
            </div>
            <input type="range" min="10" max="72" value={data.fontSize} onChange={(e) => handleUpdate('fontSize', parseInt(e.target.value) || 12)} className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#8B3DFF]" />
          </div>

          <div>
            <Label>Emphasis</Label>
            <div className="flex gap-4 bg-black/20 p-2.5 rounded-[2rem] border border-white/5 shadow-inner">
              <button onClick={() => handleUpdate('isBold', !data.isBold)} className={`flex-1 py-3 text-xs font-black rounded-2xl transition-all ${data.isBold ? 'bg-[#8B3DFF] text-white shadow-lg' : 'text-slate-600 hover:text-slate-400'}`}>B</button>
              <button onClick={() => handleUpdate('isItalic', !data.isItalic)} className={`flex-1 py-3 text-xs font-black italic rounded-2xl transition-all ${data.isItalic ? 'bg-[#8B3DFF] text-white shadow-lg' : 'text-slate-600 hover:text-slate-400'}`}>I</button>
              <button onClick={() => handleUpdate('isUnderline', !data.isUnderline)} className={`flex-1 py-3 text-xs font-black underline rounded-2xl transition-all ${data.isUnderline ? 'bg-[#8B3DFF] text-white shadow-lg' : 'text-slate-600 hover:text-slate-400'}`}>U</button>
            </div>
          </div>
        </ControlGroup>
      )}

      {data.type === 'image' && (
        <ControlGroup title="Image">
          <div>
            <Label>Source URL</Label>
            <input type="text" value={data.src} onChange={(e) => handleUpdate('src', e.target.value)} className="w-full px-6 py-4 text-xs border border-white/5 rounded-[2rem] focus:ring-4 focus:ring-[#8B3DFF]/10 focus:border-[#8B3DFF] outline-none bg-black/20 text-white transition-all shadow-inner" placeholder="https://..." />
          </div>
        </ControlGroup>
      )}

      {data.type === 'button' && (
        <ControlGroup title="Button">
          <div className="space-y-5">
            <div>
              <Label>Label</Label>
              <input type="text" value={data.text} onChange={(e) => handleUpdate('text', e.target.value)} className="w-full px-6 py-4 text-sm font-bold border border-white/5 rounded-[2rem] outline-none bg-black/20 text-white focus:bg-black/30 transition-all shadow-inner no-scrollbar" />
            </div>
            <div>
              <Label>Redirect URL</Label>
              <input type="text" value={data.url} onChange={(e) => handleUpdate('url', e.target.value)} className="w-full px-6 py-4 text-xs border border-white/5 rounded-[2rem] outline-none bg-black/20 text-white focus:bg-black/30 transition-all shadow-inner no-scrollbar" />
            </div>
          </div>

          <div className="pt-8 border-t border-white/5 space-y-5">
            <div className="flex items-center gap-4 p-5 bg-black/20 rounded-[2rem] border border-white/5 cursor-pointer group/toggle hover:bg-black/30 transition-all shadow-inner" onClick={() => handleUpdate('useGradient', !data.useGradient)}>
               <div className={`w-12 h-6 rounded-full p-1 transition-all duration-500 flex ${data.useGradient ? 'bg-[#8B3DFF] justify-end shadow-[0_0_15px_rgba(139,61,255,0.4)]' : 'bg-slate-800 justify-start'}`}>
                  <div className="w-4 h-4 bg-white rounded-full shadow-2xl" />
               </div>
               <span className="text-[10px] text-slate-500 font-black uppercase tracking-[0.2em] group-hover/toggle:text-slate-300 transition-colors">Gradient Fill</span>
            </div>
            
            {data.useGradient ? (
              <div className="flex gap-5 p-6 bg-black/30 rounded-[2.5rem] border border-white/5 shadow-2xl ring-1 ring-white/5">
                <div className="flex-1 flex flex-col items-center">
                  <span className="text-[8px] font-black text-slate-600 mb-3 uppercase tracking-tighter">Start</span>
                  <div className="relative w-9 h-9 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl ring-4 ring-black/40">
                     <input type="color" value={data.gradientStart} onChange={(e) => handleUpdate('gradientStart', e.target.value)} className="absolute inset-[-50%] w-[200%] h-[200%] cursor-pointer border-none bg-transparent" />
                  </div>
                </div>
                <div className="w-px bg-white/5 h-14 self-center shadow-inner"></div>
                <div className="flex-1 flex flex-col items-center">
                  <span className="text-[8px] font-black text-slate-600 mb-3 uppercase tracking-tighter">End</span>
                  <div className="relative w-9 h-9 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl ring-4 ring-black/40">
                     <input type="color" value={data.gradientEnd} onChange={(e) => handleUpdate('gradientEnd', e.target.value)} className="absolute inset-[-50%] w-[200%] h-[200%] cursor-pointer border-none bg-transparent" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center p-5 bg-black/20 rounded-[2rem] border border-white/5 shadow-inner">
                <Label>Fill Color</Label>
                <div className="relative w-9 h-9 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl ring-4 ring-black/20">
                   <input type="color" value={data.bgColor} onChange={(e) => handleUpdate('bgColor', e.target.value)} className="absolute inset-[-50%] w-[200%] h-[200%] cursor-pointer border-none bg-transparent" />
                </div>
              </div>
            )}
            
            <div className="flex justify-between items-center p-5 bg-black/20 rounded-[2rem] border border-white/5 shadow-inner">
              <Label>Text Tint</Label>
              <div className="relative w-9 h-9 rounded-full overflow-hidden border-2 border-white/10 shadow-2xl ring-4 ring-black/20">
                 <input type="color" value={data.color} onChange={(e) => handleUpdate('color', e.target.value)} className="absolute inset-[-50%] w-[200%] h-[200%] cursor-pointer border-none bg-transparent" />
              </div>
            </div>
          </div>
        </ControlGroup>
      )}

      {data.type !== 'divider' && data.type !== 'row' && (
        <ControlGroup title="Object Alignment">
          <div className="space-y-6">
            <div className="flex justify-between items-center px-2">
              <div className="flex flex-col items-center gap-1">
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-tight">Left</span>
              </div>
              <div className="flex flex-col items-center">
                 <span className="text-[10px] font-black text-[#8B3DFF] bg-[#8B3DFF]/10 px-4 py-1.5 rounded-full border border-[#8B3DFF]/20 uppercase tracking-[0.2em] shadow-sm shadow-[#8B3DFF]/5 transition-all">
                    {typeof data.align === 'number' 
                      ? (data.align === 0 ? 'CENTER' : data.align > 0 ? `RIGHT +${data.align}` : `LEFT ${data.align}`)
                      : (data.align || 'CENTER').toUpperCase()
                    }
                 </span>
              </div>
              <div className="flex flex-col items-center gap-1">
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-tight">Right</span>
              </div>
            </div>
            <div className="px-4 relative flex items-center h-6 group/slider">
              {/* Visual markers for the slider tracks */}
              <div className="absolute left-4 right-4 h-1 bg-white/5 rounded-full" />
              <div className="absolute left-1/2 -translate-x-1/2 w-1.5 h-4 bg-[#8B3DFF]/40 rounded-full z-0 group-hover/slider:bg-[#8B3DFF]/60 transition-colors" />
              
              <input 
                type="range" 
                min="-50" max="50" step="1"
                value={typeof data.align === 'number' ? data.align : (data.align === 'left' ? -50 : data.align === 'right' ? 50 : 0)}
                onChange={(e) => handleUpdate('align', parseInt(e.target.value))}
                className="w-full h-1.5 bg-transparent rounded-lg appearance-none cursor-pointer accent-[#8B3DFF] relative z-10 transition-all"
              />
            </div>
          </div>
        </ControlGroup>
      )}

      <ControlGroup title="Geometry & Layout">
        {selectedElement.type === 'block' && (
          <div className="mb-8 pb-8 border-b border-white/5">
            <div className="flex justify-between items-center mb-4">
              <Label>Component Width</Label>
              <span className="text-[10px] font-black text-[#8B3DFF] bg-[#8B3DFF]/10 px-3 py-1 rounded-full border border-[#8B3DFF]/20">
                {data.width === 0 ? 'AUTO' : `${data.width}PX`}
              </span>
            </div>
            <input 
              type="range" 
              min="0" max="800" step="10" 
              value={data.width || 0} 
              onChange={(e) => handleUpdate('width', parseInt(e.target.value) || 0)} 
              className="w-full h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer mb-6 accent-[#8B3DFF]" 
            />
            <div className="flex gap-2.5 bg-black/20 p-2.5 rounded-[2rem] border border-white/5 shadow-inner">
              {[{ l: 'AUTO', v: 0 }, { l: 'S', v: 200 }, { l: 'M', v: 400 }, { l: 'L', v: 600 }, { l: 'MAX', v: 800 }].map(p => (
                <button 
                  key={p.v} 
                  onClick={() => handleUpdate('width', p.v)} 
                  className={`flex-1 py-2.5 text-[9px] font-black uppercase rounded-xl transition-all ${data.width === p.v ? 'bg-[#8B3DFF] text-white shadow-lg' : 'text-slate-600 hover:text-slate-400'}`}
                >
                  {p.l}
                </button>
              ))}
            </div>
          </div>
        )}
        {renderSpacingControls(data)}
      </ControlGroup>

      <div className="px-2 pb-10">
        <button
          onClick={() => {
            if (selectedElement.type === 'block') removeBlock(selectedElement.id);
            else if (selectedElement.type === 'row') removeRow(selectedElement.id);
          }}
          className="w-full py-5 rounded-[2.5rem] border border-red-500/10 bg-red-500/5 text-red-500 text-[10px] font-black uppercase tracking-[0.3em] hover:bg-red-500 hover:text-white hover:border-red-500 active:scale-[0.97] transition-all duration-500 shadow-2xl shadow-red-500/10 flex items-center justify-center gap-4 group/btn overflow-hidden relative"
        >
          <div className="absolute inset-0 bg-red-500 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-500 -z-10" />
          <svg className="w-5 h-5 transition-transform duration-500 group-hover/btn:rotate-12 group-hover/btn:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          <span className="relative z-10">Remove {selectedElement.type}</span>
        </button>
      </div>
    </div>
  );
};

export default PropertyEditor;
